import type { CrmContactRow } from "../../@types/webotServer";
import type { CrmStatus, CustomerSeriousness } from "../../@types/sharedSchema";

/** The sparse shape `buildCrmStatusUpdate` returns — only the axes the write touches. */
export type CrmStatusWrite = {
  crmStatus?: CrmStatus | null;
  crmSubStatus?: string | null;
  crmIsHot?: boolean | null;
  customerSeriousness?: CustomerSeriousness | null;
};

/**
 * The OPTIMISTIC row patch for a status/hot write — sparse, exactly like the write.
 *
 * `buildCrmStatusUpdate` deliberately returns only the axes the caller touched (plus the
 * recomputed legacy mirror), and Firestore's `set(update, { merge: true })` honours that
 * sparseness. The optimistic patch used to be built DENSELY from the same object —
 * `crmIsHot: Boolean(update.crmIsHot)`, `crmStatus: update.crmStatus ?? null` — which
 * turned every absent key into an explicit false/null on the row:
 *
 *   - setting a STATUS repainted the 🔥 OFF (the write never touched it);
 *   - toggling the 🔥 repainted the STATUS as cleared (ditto).
 *
 * The document was right the whole time — only the row lied, until something re-synced
 * it. On desktop the open panel's live-listener projection healed the row within a
 * second, which is exactly why this shipped: the mobile list has no panel open, so the
 * lie just sat there (pilot report, 2026-08-24: "setting a status toggles the hot off…
 * toggling hot removes the status… doesn't sync"). A pure function so the test can pin
 * the sparseness without mocking Firestore.
 */
export const optimisticCrmRowPatch = (update: CrmStatusWrite): Partial<CrmContactRow> => {
  const patch: Partial<CrmContactRow> = {};
  if (update.crmStatus !== undefined) patch.crmStatus = (update.crmStatus ?? null) as string | null;
  if (update.crmSubStatus !== undefined) patch.crmSubStatus = update.crmSubStatus ?? null;
  if (update.crmIsHot !== undefined) patch.crmIsHot = Boolean(update.crmIsHot);
  if (update.customerSeriousness !== undefined) {
    patch.customerSeriousness = (update.customerSeriousness ?? null) as string | null;
  }
  return patch;
};
